import compose from 'lodash/flowRight'
import * as React from 'react'
import {useContext, useMemo} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {fetchBuildsData} from '../../../../../../actions/builds'
import BuildsFetcher from '../../../../../../containers/BuildsFetcher'
import {withBranchProps} from '../../../../../../hooks/routes'
import {getBranchesLocator, Policy} from '../../../../../../rest/branches'
import {getBuildsInited, getBuildsReady, makeGetBuilds} from '../../../../../../selectors'
import {getBuildTypeFilter, stringifyId} from '../../../../../../types'
import {noop} from '../../../../../../utils/empty'
import {defaultRequestPoolSize, RequestsPool} from '../../../../../../utils/requestQueue'
import {
  SequenceLoaderContext,
  useSequenceLoaderReadyHandler,
} from '../../../../../common/SequenceLoader/SequenceLoader.hooks'
import {useParentVisibility} from '../../../../../common/Visible/Visible.hooks'

import type {BuildTypeLineBranchesCountQuery} from './__generated__/BuildTypeLineBranchesCountQuery.graphql'
import BuildTypeLineBuilds from './BuildTypeLine.builds'
import {getBuildTypeLineLocator} from './BuildTypeLine.selectors'
import type {FetcherOwnProps} from './BuildTypeLine.types'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'
import {internalProps} from 'src/types/BS_types'

const queue = new RequestsPool(
  internalProps['teamcity.ui.requestPoolSize'] ?? defaultRequestPoolSize,
)

const branchesCountQuery = graphql`
  query BuildTypeLineBranchesCountQuery($locator: String, $btLocator: String!) {
    branchesOfBuildType(locator: $locator, btLocator: $btLocator) {
      count
    }
  }
`

function BuildTypeLineFetcher(props: FetcherOwnProps) {
  const {buildTypeId, branch, onLoadingComplete, ...restProps} = props
  const {branchesOfBuildType} = useLazyLoadQuery<BuildTypeLineBranchesCountQuery>(
    branchesCountQuery,
    {
      locator: getBranchesLocator({
        node: getBuildTypeFilter(buildTypeId),
        policy: Policy.ACTIVE_HISTORY_AND_ACTIVE_VCS_BRANCHES,
      }),
      btLocator: `id:${buildTypeId}`,
    },
  )
  const locator = getBuildTypeLineLocator(props, branchesOfBuildType?.count ?? 1)
  const getBuilds = React.useMemo(makeGetBuilds, [])
  const builds = useAppSelector(state => getBuilds(state, locator))
  const ready = useAppSelector(state => getBuildsReady(state, locator))
  const inited = useAppSelector(state => getBuildsInited(state, locator))
  const isVisible = useParentVisibility()
  useSequenceLoaderReadyHandler(ready)
  const {isLoading} = useContext(SequenceLoaderContext)
  const showLoader = isLoading || !ready
  const dispatch = useAppDispatch()
  React.useEffect(() => {
    if (isVisible === false && !inited) {
      let unsubscribeCache = noop
      queue.add(stringifyId(buildTypeId), () => {
        unsubscribeCache()
        const result = dispatch(
          fetchBuildsData({
            locator,
            withPager: false,
            requestOptions: {
              withBuildTypeDetails: true,
              withSnapshotDependencies: false,
              withQueuedInfo: true,
              withRunningInfo: true,
            },
            inBackground: true,
          }),
        )
        unsubscribeCache = result.unsubscribe
        return result
      })
      return () => {
        queue.cancel(stringifyId(buildTypeId))
        unsubscribeCache()
      }
    }

    return undefined
  }, [buildTypeId, dispatch, inited, isVisible, locator])

  React.useEffect(() => {
    if (ready && typeof onLoadingComplete === 'function') {
      onLoadingComplete(builds.length !== 0)
    }
  }, [builds.length, onLoadingComplete, ready])

  const shouldRenderBuilds = builds.length > 0 || showLoader
  const children = useMemo(
    () =>
      shouldRenderBuilds ? (
        <BuildTypeLineBuilds
          ready={!showLoader}
          builds={builds}
          buildTypeId={buildTypeId}
          branch={branch}
          nested={props.nested}
        />
      ) : null,
    [branch, buildTypeId, builds, props.nested, shouldRenderBuilds, showLoader],
  )
  return (
    <BuildsFetcher
      pause={!isVisible}
      buildTypeId={buildTypeId}
      withQueued
      withRunning
      locator={locator}
      {...restProps}
    >
      {children}
    </BuildsFetcher>
  )
}

export default compose(withBranchProps, React.memo)(BuildTypeLineFetcher)
